/**
 * Barycentric coordinate utilities for arbitrary axis annotation.
 *
 * Allows coaches to click arbitrary points on the video and anchor them
 * to body landmarks using barycentric coordinates relative to a triangle
 * of nearby landmarks. This makes anchors body-relative rather than
 * pixel-relative, so they follow the athlete across frames.
 */

import type { TemplateAnchor } from '@/types/api';
import { LANDMARK_NAMES } from '@/utils/landmarkDetection';

// ---------------------------------------------------------------------------
// Core barycentric math
// ---------------------------------------------------------------------------

/**
 * Compute barycentric weights for a point relative to a triangle.
 *
 * Returns [lambda1, lambda2, lambda3] such that:
 *   point = lambda1 * A + lambda2 * B + lambda3 * C
 * and lambda1 + lambda2 + lambda3 ~= 1.0
 *
 * Uses the standard 2D signed-area method.
 */
export function computeBarycentricWeights(
  point: [number, number],
  triangle: [[number, number], [number, number], [number, number]],
): [number, number, number] {
  const [px, py] = point;
  const [[ax, ay], [bx, by], [cx, cy]] = triangle;

  // Area of the full triangle (signed, using cross product)
  const denom = (by - cy) * (ax - cx) + (cx - bx) * (ay - cy);

  if (Math.abs(denom) < 1e-10) {
    // Degenerate triangle: return uniform weights as a safe fallback
    return [1 / 3, 1 / 3, 1 / 3];
  }

  const lambda1 = ((by - cy) * (px - cx) + (cx - bx) * (py - cy)) / denom;
  const lambda2 = ((cy - ay) * (px - cx) + (ax - cx) * (py - cy)) / denom;
  const lambda3 = 1.0 - lambda1 - lambda2;

  return [lambda1, lambda2, lambda3];
}

/**
 * Reconstruct a point from barycentric weights and a triangle.
 *
 * Returns [x, y] = w1*A + w2*B + w3*C
 */
export function reconstructPoint(
  weights: [number, number, number],
  triangle: [[number, number], [number, number], [number, number]],
): [number, number] {
  const [w1, w2, w3] = weights;
  const [[ax, ay], [bx, by], [cx, cy]] = triangle;

  return [w1 * ax + w2 * bx + w3 * cx, w1 * ay + w2 * by + w3 * cy];
}

// ---------------------------------------------------------------------------
// Triangle selection
// ---------------------------------------------------------------------------

export interface NearestTriangleResult {
  indices: [number, number, number];
  positions: [[number, number], [number, number], [number, number]];
}

/**
 * Select the nearest non-degenerate triangle of landmarks to the clicked point.
 *
 * Algorithm:
 * 1. Filter landmarks by visibility >= visibilityThreshold (default 0.5)
 * 2. Sort by Euclidean distance to the clicked point
 * 3. Take the 3 nearest and check non-degeneracy (triangle area > 0.001, matches backend)
 * 4. If degenerate, try the next candidate combination
 *
 * Returns null if no valid triangle can be formed (e.g. fewer than 3 visible landmarks).
 */
export function selectNearestTriangle(
  point: [number, number],
  landmarks: Array<{ x: number; y: number; visibility: number }>,
  visibilityThreshold: number = 0.5,
): NearestTriangleResult | null {
  const [px, py] = point;

  // Filter and annotate with index and distance
  const candidates = landmarks
    .map((lm, idx) => ({ idx, lm, dist: Math.hypot(lm.x - px, lm.y - py) }))
    .filter(({ lm }) => lm.visibility >= visibilityThreshold)
    .sort((a, b) => a.dist - b.dist);

  if (candidates.length < 3) return null;

  // Try combinations starting from the 3 nearest, incrementally broadening
  // We try all combinations of the top min(candidates.length, 8) candidates
  // to avoid an O(n^3) search on large sets.
  const pool = candidates.slice(0, Math.min(candidates.length, 8));

  for (let i = 0; i < pool.length - 2; i++) {
    for (let j = i + 1; j < pool.length - 1; j++) {
      for (let k = j + 1; k < pool.length; k++) {
        const A = pool[i];
        const B = pool[j];
        const C = pool[k];

        // Check non-degeneracy via 2D cross product magnitude.
        // area = triangle area = 0.5 * |AB × AC|. Threshold 0.001 matches the backend.
        const abx = B.lm.x - A.lm.x;
        const aby = B.lm.y - A.lm.y;
        const acx = C.lm.x - A.lm.x;
        const acy = C.lm.y - A.lm.y;
        const area = 0.5 * Math.abs(abx * acy - aby * acx);

        if (area > 0.001) {
          return {
            indices: [A.idx, B.idx, C.idx],
            positions: [
              [A.lm.x, A.lm.y],
              [B.lm.x, B.lm.y],
              [C.lm.x, C.lm.y],
            ],
          };
        }
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// High-level anchor creation / reconstruction
// ---------------------------------------------------------------------------

/**
 * Create a TemplateAnchor from a normalized click position and pose data.
 *
 * @param normalizedX - Click x in 0-1 image space
 * @param normalizedY - Click y in 0-1 image space
 * @param landmarks   - Array of landmarks for the current frame (normalized coords)
 * @param landmarkNames - Map from index to landmark name (LANDMARK_NAMES from landmarkDetection.ts)
 * @param frameRef    - Frame index this anchor was created on
 * @returns TemplateAnchor or null if no valid triangle found
 */
export function createAnchorFromClick(
  normalizedX: number,
  normalizedY: number,
  landmarks: Array<{ x: number; y: number; visibility: number }>,
  landmarkNames: { [key: number]: string },
  frameRef: number,
): TemplateAnchor | null {
  const result = selectNearestTriangle([normalizedX, normalizedY], landmarks);
  if (!result) return null;

  const weights = computeBarycentricWeights(
    [normalizedX, normalizedY],
    result.positions,
  );

  const triNames: [string, string, string] = [
    landmarkNames[result.indices[0]] ?? `landmark_${result.indices[0]}`,
    landmarkNames[result.indices[1]] ?? `landmark_${result.indices[1]}`,
    landmarkNames[result.indices[2]] ?? `landmark_${result.indices[2]}`,
  ];

  return {
    frame_ref: frameRef,
    anchor_method: 'barycentric_triangle',
    triangle_landmarks: triNames,
    barycentric_weights: weights,
    anchor_in_coords: 'image',
  };
}

/**
 * Reconstruct the anchor's [x, y] position in the current frame using pose data.
 *
 * @param anchor              - The TemplateAnchor to reconstruct
 * @param landmarks           - Landmarks for the current frame
 * @param landmarkNameToIndex - Map from landmark name to index
 * @returns Normalized [x, y] or null if landmarks are unavailable
 */
export function reconstructAnchorFromPose(
  anchor: TemplateAnchor,
  landmarks: Array<{ x: number; y: number; visibility: number }>,
  landmarkNameToIndex: Record<string, number>,
): [number, number] | null {
  const [nameA, nameB, nameC] = anchor.triangle_landmarks;

  const idxA = landmarkNameToIndex[nameA];
  const idxB = landmarkNameToIndex[nameB];
  const idxC = landmarkNameToIndex[nameC];

  if (idxA === undefined || idxB === undefined || idxC === undefined) return null;

  const lmA = landmarks[idxA];
  const lmB = landmarks[idxB];
  const lmC = landmarks[idxC];

  if (!lmA || !lmB || !lmC) return null;
  // Use 0.5 to match selectNearestTriangle and the backend's _reconstruct_anchor_from_numpy.
  // This prevents the preview from showing positions that the backend scoring would skip.
  if (lmA.visibility < 0.5 || lmB.visibility < 0.5 || lmC.visibility < 0.5) return null;

  const p1: [number, number] = [lmA.x, lmA.y];
  const p2: [number, number] = [lmB.x, lmB.y];
  const p3: [number, number] = [lmC.x, lmC.y];

  const triangle: [[number, number], [number, number], [number, number]] = [p1, p2, p3];

  // Two-tier weight guard (matches backend _reconstruct_anchor_from_numpy).
  // Near-degenerate triangles get a stricter extrapolation limit.
  const MIN_TRIANGLE_AREA = 0.001;
  const RELAXED_WEIGHT_MAGNITUDE = 3.0;
  const STRICT_WEIGHT_MAGNITUDE = 1.5;

  const edge1 = [p2[0] - p1[0], p2[1] - p1[1]];
  const edge2 = [p3[0] - p1[0], p3[1] - p1[1]];
  const triangleArea = 0.5 * Math.abs(edge1[0] * edge2[1] - edge1[1] * edge2[0]);

  const weightLimit = triangleArea >= MIN_TRIANGLE_AREA ? RELAXED_WEIGHT_MAGNITUDE : STRICT_WEIGHT_MAGNITUDE;
  const weights = anchor.barycentric_weights;
  if (weights.some(w => Math.abs(w) > weightLimit)) {
    console.warn('reconstructAnchorFromPose: extrapolation detected', { weights, triangleArea });
    return null;
  }

  return reconstructPoint(weights, triangle);
}

// Re-export LANDMARK_NAMES for convenience so callers don't need two imports
export { LANDMARK_NAMES };
